import { html, css, LitElement } from 'lit-element';
import { db } from './intrivity_app.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';

import { emitEvent, hasRole, INTRIVITY_NAV_SELECT } from './utility.js';
import { t, Localized } from './i18n.js';

import '@material/mwc-button';
import '@material/mwc-textfield';

// The hardcoded fallback in functions/ai.js -- shown here only so the field
// can describe what "left blank" means; this file never needs to duplicate
// it as a real default value, since an empty gemini_model is handled
// entirely on the backend (resolveGeminiModel in functions/ai.js).
const DEFAULT_MODEL_HINT = 'gemini-flash-latest';

// SuperAdmin-only editor for which Gemini model the AI Summary Report
// feature uses -- Firestore doc config/ai, read live (no redeploy needed)
// by functions/ai.js's resolveGeminiModel on every generateAiSummary call.
// The @google/generative-ai SDK has no way to list or validate model names,
// so this is a plain text field rather than a dropdown of guessed model
// IDs -- an invalid entry only surfaces as an error the next time someone
// generates a summary.
export class IntrivityAdminAiConfig extends Localized(LitElement) {
  static get properties() {
    return {
      user_profile: { type: Object },
      _modelName: { type: String, state: true },
      _isLoading: { type: Boolean, state: true },
      _isSaving: { type: Boolean, state: true },
    };
  }

  constructor() {
    super();
    this._modelName = '';
    this._isLoading = true;
    this._isSaving = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    this._isLoading = true;
    try {
      const snap = await getDoc(doc(db, 'config', 'ai'));
      const data = snap.exists() ? snap.data() : null;
      this._modelName = (data && data.gemini_model) || '';
    } catch (e) {
      console.error('Could not load the AI model configuration:', e);
      alert(t('aiConfig.loadError', { message: e.message || e }));
    } finally {
      this._isLoading = false;
    }
  }

  _handleInput(e) {
    this._modelName = e.target.value.trim();
  }

  async _save() {
    if (!hasRole('superadmin', this.user_profile)) return;
    this._isSaving = true;
    try {
      await setDoc(doc(db, 'config', 'ai'), { gemini_model: this._modelName }, { merge: true });
      alert(t('aiConfig.saved'));
    } catch (e) {
      console.error('Error saving the AI model configuration:', e);
      alert(t('aiConfig.saveError', { message: e.message || e }));
    } finally {
      this._isSaving = false;
    }
  }

  static get styles() {
    return css`
      mwc-textfield {
        width: 100%;
        max-width: 360px;
        margin-top: 8px;
      }
      .hint {
        font-size: 0.85em;
        color: #666;
        margin-top: 0;
      }
    `;
  }

  render() {
    if (!hasRole('superadmin', this.user_profile)) return html``;
    return html`
      <div style="margin: 10px; max-width: 720px;">
        <h3>${t('aiConfig.title')}</h3>
        <p class="hint">
          ${t('aiConfig.hint', { model: DEFAULT_MODEL_HINT })}
        </p>
        <p class="hint">
          <a href="#" @click=${(e) => { e.preventDefault(); emitEvent(INTRIVITY_NAV_SELECT, { selection: 'ai-models' }, this); }}>
            ${t('aiConfig.viewModels')}
          </a>
        </p>
        ${this._isLoading ? html`<p>${t('common.loading')}</p>` : html`
          <mwc-textfield
            label=${t('aiConfig.modelLabel')}
            placeholder=${DEFAULT_MODEL_HINT}
            .value=${this._modelName}
            @input=${this._handleInput}
            ?disabled=${this._isSaving}>
          </mwc-textfield>
          <div>
            <mwc-button raised label="${this._isSaving ? t('common.saving') : t('common.save')}" @click=${this._save} ?disabled=${this._isSaving}></mwc-button>
          </div>
        `}
      </div>
    `;
  }
}
if (!customElements.get('intrivity-admin-ai-config')) customElements.define('intrivity-admin-ai-config', IntrivityAdminAiConfig);
